import React from 'react'
import PropTypes from 'prop-types'
import { css } from 'glamor'
import InlineSVG from 'svg-inline-react'
import { Text } from '@paypalcorp/pp-react'
import content from 'pp-react-l10n'
import { connect } from 'transfer/utils/p2p-connect'

import Badges from './badges'
import appPushBgSVG from './icons/appPushBG.svg'
import { Modal } from '../../../react-components/modal/modal'
import { ignoreMaxViews } from '../../../experiments/appPush'
import useDeviceWidth from '../../../lib/styles/useDeviceWidth'
import {
  trackAppPushImpression,
  trackAppPushClick,
  PAGE_ACTIONS,
} from '../../utils/appPushAnalytics'

const i18n = content('transfer/appBanner')

const classes = {
  modalContent: css({
    '& .modal-content-container': {
      padding: '0 2rem',
    },
  }).toString(),
}

// This handles the max number of views a user can see the modal.
// We use localstorage because this is handled PER device.
// Note: getItem returns null if key doesn't exist: Number(null) === 0
const ls = window.localStorage
const modalViews = {
  getCount: () => (ls ? Number(ls.getItem('p2pAppPushModalCount')) : 0),
  setCount: count => ls && ls.setItem('p2pAppPushModalCount', `${count}`),
  increase: () => modalViews.setCount(modalViews.getCount() + 1),
}

export function AppPushModal({
  isOtherOverpanelOpened = false,
  showAppBanner = false,
  appPushModalDelay = 1800,
  appPushModalMaxViews = 3,
}) {
  const [isOpen, setIsOpen] = React.useState(false)
  const handleClose = () => {
    trackAppPushClick(PAGE_ACTIONS.CLOSE)
    setIsOpen(false)
  }
  const handleClick = () => {
    trackAppPushClick(PAGE_ACTIONS.DOWNLOAD)
  }

  const overpanelCount = modalViews.getCount()
  const hasReachedMaxViews =
    !ignoreMaxViews() && overpanelCount >= appPushModalMaxViews
  const doNotRenderModal =
    isOtherOverpanelOpened || !showAppBanner || hasReachedMaxViews

  const deviceWidth = useDeviceWidth()
  const isSmall = deviceWidth <= 360

  React.useEffect(() => {
    if (doNotRenderModal) {
      return
    }

    // Delay open
    const timeoutId = window.setTimeout(() => {
      setIsOpen(true)
      trackAppPushImpression()
      if (overpanelCount < appPushModalMaxViews) {
        modalViews.increase()
      }
    }, appPushModalDelay)

    // in case the component is unloaded before the timeout executes
    return () => window.clearTimeout(timeoutId)
  }, [])

  if (doNotRenderModal) {
    return null
  }

  return (
    <Modal
      isOpen={isOpen}
      handleClose={handleClose}
      renderBanner={() => <InlineSVG src={appPushBgSVG} />}
      className={classes.modalContent}
    >
      <Text
        as="h2"
        size={isSmall ? 'lg' : 'xl2'}
        regular={!isSmall}
        style={{ marginBottom: '0.5rem' }}
      >
        {i18n('appBanner.new.header.ios')}
      </Text>
      <Text
        size="md"
        style={{
          marginTop: '0',
          marginBottom: isSmall ? '1rem' : '2rem',
        }}
      >
        {i18n('appBanner.new.description.ios')}
      </Text>
      <Badges
        onClick={handleClick}
        style={{
          fontSize: isSmall ? '3rem' : '3.5rem',
        }}
      />
    </Modal>
  )
}

AppPushModal.propTypes = {
  isOtherOverpanelOpened: PropTypes.bool,
  showAppBanner: PropTypes.bool,
  appPushModalDelay: PropTypes.number,
  appPushModalMaxViews: PropTypes.number,
}

export default connect(({ server, overpanels }) => ({
  isOtherOverpanelOpened: overpanels.openOverpanelCount > 0,
  showAppBanner: server.showAppBanner,
  appPushModalDelay: server.appPushModalDelay,
  appPushModalMaxViews: server.appPushModalMaxViews,
}))(AppPushModal)
